import { IonContent, IonItem, IonLabel, IonModal } from "@ionic/react";
import React, { useRef, useState } from "react";
import { Helmet } from "react-helmet";

const wordProblems = [
    "You have {num1} apples. You buy {num2} more. How many do you have now?",
    "There were {num1} balloons at the party. You added {num2} more. How many are there in total?",
    "A farmer had {num1} cows. He bought {num2} more. How many does he have now?",
    "You had {num1} toy cars. Your friend gave you {num2} more. How many do you have now?",
    "A basket had {num1} oranges. You put {num2} more inside. How many are there now?",
    "A school library had {num1} books. They received {num2} more. How many are there in total?",
    "A shop had {num1} cupcakes. They baked {num2} more. How many are there now?",
    "You had {num1} chocolates. Your mother gave you {num2} more. How many do you have now?",
    "There were {num1} fish in the pond. {num2} more were added. How many are there now?",
    "A toy store had {num1} teddy bears. They stocked {num2} more. How many are there now?",
    "You collected {num1} stickers. Your friend gave you {num2} more. How many do you have now?",
    "A bakery made {num1} loaves of bread in the morning and {num2} more in the evening. How many in total?",
    "A gardener planted {num1} flowers. He planted {num2} more the next day. How many are there in total?",
    "There were {num1} crayons in the box. You added {num2} more. How many are there now?",
    "A farmer had {num1} sheep. He bought {num2} more. How many sheep does he have now?",
    "You had {num1} pencils in your case. Your teacher gave you {num2} more. How many in total?",
    "A store had {num1} soccer balls. They received {num2} more as stock. How many do they have now?",
    "Your toy train set had {num1} tracks. You found {num2} more under the bed. How many do you have now?",
    "There were {num1} cookies on the plate. You baked {num2} more. How many are there now?",
    "You collected {num1} seashells at the beach. Your friend gave you {num2} more. How many do you have now?",
    "A farmer had {num1} ducks in the pond. {num2} more joined. How many are there now?",
    "A store had {num1} hats. They got {num2} more in a new shipment. How many hats in total?",
    "You had {num1} marbles. Your brother gave you {num2} more. How many do you have now?",
    "A class had {num1} students. {num2} more joined in the new semester. How many are in the class now?",
    "A zoo had {num1} monkeys. They got {num2} more from another zoo. How many monkeys in total?",
    "A movie theater had {num1} seats. They added {num2} more. How many seats are there now?",
    "There were {num1} ducks in the lake. {num2} more flew in. How many are there now?",
    "A soccer team had {num1} players. {num2} more joined. How many players are there in total?",
    "A grocery store had {num1} milk cartons. They received {num2} more. How many do they have now?",
    "A magician had {num1} magic wands. He found {num2} more. How many wands does he have now?",
    "You had {num1} golden coins in your treasure chest. You found {num2} more. How many coins now?",
    "There were {num1} toy dinosaurs on the shelf. {num2} more were added. How many are there now?",
    "A classroom had {num1} desks. They brought in {num2} more. How many desks in total?",
    "You had {num1} books. Your friend gave you {num2} more. How many do you have now?",
    "A pet store had {num1} puppies. They got {num2} more. How many puppies in total?",
    "There were {num1} umbrellas in the stand. {num2} more were added. How many are there now?",
    "A spaceship had {num1} astronauts on board. {num2} more joined. How many in total?",
    "A baker had {num1} cakes. He made {num2} more. How many cakes are there now?",
    "A playground had {num1} swings. They installed {num2} more. How many swings in total?",
    "There were {num1} cars in the parking lot. {num2} more arrived. How many in total?",
    "You had {num1} candles. You bought {num2} more. How many do you have now?",
    "There were {num1} birds in the tree. {num2} more perched there. How many birds in total?",
    "You had {num1} notebooks. Your teacher gave you {num2} more. How many do you have now?",
    "A farm had {num1} hens. They bought {num2} more. How many hens are there now?",
    "A library had {num1} dictionaries. They got {num2} more. How many are there now?",
    "A toy shop had {num1} kites. They received {num2} more. How many are there in total?",
    "A theater had {num1} actors. {num2} more joined the play. How many actors in total?",
    "There were {num1} spoons in the kitchen. You bought {num2} more. How many are there now?",
    "A bus had {num1} passengers. {num2} more got in. How many passengers are there now?",
    "A candy shop had {num1} lollipops. They stocked {num2} more. How many lollipops now?"
];


const getRandomNumber = (min: number, max: number) => Math.floor(Math.random() * (max - min + 1)) + min;

const generateQuestion = () => {
    const num1 = getRandomNumber(1, 9);
    const num2 = getRandomNumber(1, 9);
    const questionTemplate = wordProblems[Math.floor(Math.random() * wordProblems.length)];
    const question = questionTemplate.replace("{num1}", num1.toString()).replace("{num2}", num2.toString());
    const correctAnswer = num1 + num2;

    const options = new Set<number>();
    options.add(correctAnswer);
    while (options.size < 3) {
        options.add(getRandomNumber(2, 18));
    }

    return {
        question,
        correctAnswer,
        options: Array.from(options).sort(() => Math.random() - 0.5) // Shuffle options
    };
};

const AdditionWordProblem: React.FC = () => {
    const [quiz, setQuiz] = useState(generateQuestion());
    const [feedback, setFeedback] = useState("");
    const [showImage, setShowImage] = useState(false);
    const modal = useRef<HTMLIonModalElement>(null);
    document.title = "UKG Addition Word Problems | Real-Life Math with Fun Stories";

    const handleAnswerClick = (option: number) => {
        if (option === quiz.correctAnswer) {
            setShowImage(true);
            setTimeout(() => {
                setQuiz(generateQuestion());
                setShowImage(false);
                setFeedback("");
            }, 5000);
        } else {
            setShowImage(false);
            setFeedback("❌ Oops! Try again.");
        }
    };

    return (
        <>
            <Helmet>
                <meta name="description" content="Explore fun and easy addition word problems for UKG kids. Help your child solve real-life math problems with pictures and stories. Free worksheets available!" />
                <link rel="canonical" href="https://mamasguidance.com/ukg/mathematics/addition/word-problem" />
            </Helmet>
            {showImage === false ? (
                <>
                    <div className="contentPart additionWord pickColor matchImage match-container" style={{ textAlign: "center", padding: "20px" }}>
                        <div className="showInfo" style={{ width: '80%' }} onClick={() => modal.current?.present()}><i className="fas fa-info-circle"></i></div>
                        <IonModal
                            ref={modal}
                            trigger="open-modal"
                            initialBreakpoint={1}
                            breakpoints={[0, 0.5, 0.75, 1]}
                            handleBehavior="cycle"
                            backdropDismiss={false}  // ✅ Prevents click-to-close
                            className="full-page-modal"
                        >
                            <IonContent className="ion-padding">
                                <div style={{ textAlign: 'right', fontSize: '25px' }} onClick={() => modal.current?.dismiss()}><i className="fas fa-3a fa-times"></i></div>
                                <h1>Addition Word Problems</h1>

                                <p>
                                    Help your child apply math to everyday situations with our <strong>UKG Addition Word Problems</strong>. These fun and engaging activities combine reading comprehension with number skills, allowing children to solve simple real-life problems using addition.
                                </p>

                                <h2>🔍 What Are Word Problems in Addition?</h2>
                                <p>
                                    Word problems describe real-world scenarios where children need to <strong>read, understand, and solve</strong> using addition. They improve logical thinking, comprehension, and number sense.
                                </p>

                                <p><strong>Example:</strong> “Sara has 3 apples. Her mom gives her 2 more. How many apples does she have now?” → <strong>Answer: 5</strong></p>

                                <h2>🎯 Learning Objectives</h2>
                                <ul>
                                    <li>Improve reading and comprehension skills</li>
                                    <li>Develop problem-solving and reasoning abilities</li>
                                    <li>Understand real-world uses of math</li>
                                    <li>Apply addition knowledge to story-based situations</li>
                                </ul>


                                <h2>🔗 Related Topics</h2>
                                <div>
                                    <IonItem routerLink="/ukg/mathematics/smallest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Smallest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/biggest-number"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Biggest Number</IonLabel>
                                    </IonItem>
                                    <IonItem routerLink="/ukg/mathematics/number-comparison"
                                        routerDirection="none"
                                        lines="none"
                                        detail={true}>
                                        <IonLabel>Number Comparison</IonLabel>
                                    </IonItem>
                                </div>

                                <h2>💡 How to Teach Word Problems at Home</h2>
                                <ul>
                                    <li>Use everyday scenarios: <strong>“You ate 2 cookies and then 3 more. How many?”</strong></li>
                                    <li>Draw simple stick figures or pictures to illustrate the story</li>
                                    <li>Let children use fingers, objects, or blocks to solve</li>
                                    <li>Encourage reading the problem out loud slowly</li>
                                </ul>

                                <div className="btn hero-btn" onClick={() => modal.current?.dismiss()}>Close</div>
                            </IonContent>
                        </IonModal>
                        <article className="visually-hidden">
                            <h1>Addition Word Problems</h1>

                            <p>
                                Help your child apply math to everyday situations with our <strong>UKG Addition Word Problems</strong>. These fun and engaging activities combine reading comprehension with number skills, allowing children to solve simple real-life problems using addition.
                            </p>

                            <h2>🔍 What Are Word Problems in Addition?</h2>
                            <p>
                                Word problems describe real-world scenarios where children need to <strong>read, understand, and solve</strong> using addition. They improve logical thinking, comprehension, and number sense.
                            </p>

                            <p><strong>Example:</strong> “Sara has 3 apples. Her mom gives her 2 more. How many apples does she have now?” → <strong>Answer: 5</strong></p>

                            <h2>🎯 Learning Objectives</h2>
                            <ul>
                                <li>Improve reading and comprehension skills</li>
                                <li>Develop problem-solving and reasoning abilities</li>
                                <li>Understand real-world uses of math</li>
                                <li>Apply addition knowledge to story-based situations</li>
                            </ul>


                            <h2>🔗 Related Topics</h2>
                            <div>
                                <IonItem routerLink="/ukg/mathematics/smallest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Smallest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/biggest-number"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Biggest Number</IonLabel>
                                </IonItem>
                                <IonItem routerLink="/ukg/mathematics/number-comparison"
                                    routerDirection="none"
                                    lines="none"
                                    detail={true}>
                                    <IonLabel>Number Comparison</IonLabel>
                                </IonItem>
                            </div>

                            <h2>💡 How to Teach Word Problems at Home</h2>
                            <ul>
                                <li>Use everyday scenarios: <strong>“You ate 2 cookies and then 3 more. How many?”</strong></li>
                                <li>Draw simple stick figures or pictures to illustrate the story</li>
                                <li>Let children use fingers, objects, or blocks to solve</li>
                                <li>Encourage reading the problem out loud slowly</li>
                            </ul>
                        </article>
                        <h2>{quiz.question}</h2>
                        <div style={{ marginBottom: "20px", display: 'flex', flexWrap: 'wrap' }}>
                            {quiz.options.map((option, index) => (
                                <div className="background-blue" key={index} onClick={() => handleAnswerClick(option)} style={{ margin: "5px", padding: "10px", width: "60px" }}>
                                    {option}
                                </div>
                            ))}
                        </div>
                        {feedback && <p>{feedback}</p>}
                    </div>
                </>
            ) : (
                <>
                    <div className="contentPart showMiddle">
                        <img style={{ height: '150px' }} className='img-fluid' src={process.env.REACT_APP_WELLDONE_LINK} />
                    </div>
                </>
            )}
        </>
    );
};

export default AdditionWordProblem;
